import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n/LanguageContext';
import { useLocalizedBlogPosts } from '../i18n/contentLocale';
import { useBlogPosts } from '../hooks/use-blog';
import { usePaged } from '../hooks/use-paged';
import Pagination from '../components/Pagination';

export default function Blog() {
  const t = useT();
  const { posts: rawPosts, isStatic } = useBlogPosts();
  // The API already localises via ?lang=; the overlay only applies to static content.
  const localized = useLocalizedBlogPosts(rawPosts);
  const posts = isStatic ? localized : rawPosts;
  const ALL = '__all__';
  const [active, setActive] = React.useState(ALL);

  const categories = React.useMemo(() => {
    const counts = posts.reduce((acc, p) => {
      const c = p.category || 'Otros';
      acc[c] = (acc[c] || 0) + 1;
      return acc;
    }, {});
    return [
      { value: ALL, label: t('blogPage.all'), count: posts.length },
      ...Object.entries(counts).map(([value, count]) => ({ value, label: value, count })),
    ];
  }, [posts, t]);

  const filtered = active === ALL
    ? posts
    : posts.filter((p) => (p.category || 'Otros') === active);

  const { pageItems, page, setPage, pageCount, pageSize, setPageSize } = usePaged(filtered, 9);
  const changePage = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div className="pt-24 pb-20 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#c8a25a] text-sm font-semibold tracking-widest uppercase mb-2">{t('blog.eyebrow')}</p>
          <h1 className="text-4xl md:text-6xl font-bold text-[#14213d]">{t('blogPage.heading')}</h1>
          <p className="text-gray-500 mt-4 max-w-2xl mx-auto">{t('blogPage.subtitle')}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          {/* Filtro por temas */}
          <aside className="lg:w-56 lg:shrink-0">
            <div className="lg:sticky lg:top-28">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3 px-2">{t('blogPage.topics')}</p>
              <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                {categories.map((c) => {
                  const isActive = active === c.value;
                  return (
                    <button
                      key={c.value}
                      onClick={() => setActive(c.value)}
                      className={`flex items-center justify-between gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                        isActive
                          ? 'bg-[#1fa5a3] text-white shadow-[0_10px_25px_-12px_rgba(31,165,163,0.7)]'
                          : 'bg-gray-50 text-[#14213d] hover:bg-gray-100'
                      }`}
                    >
                      <span>{c.label}</span>
                      <span className={`text-xs font-bold ${isActive ? 'text-white/80' : 'text-gray-400'}`}>{c.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Listado */}
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {pageItems.map((p) => (
                <Link key={p.id} to={`/blog/${p.id}`} className="block group">
                  <article className="cursor-pointer h-full">
                    <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-5 owa-card shadow-[0_10px_30px_-15px_rgba(11,33,61,0.15)]">
                      <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover owa-card-image" />
                      <span className="absolute top-4 left-4 px-3 py-1 rounded-md bg-white/95 text-[11px] font-bold tracking-wider text-[#1fa5a3]">{p.category}</span>
                      <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowUpRight className="w-4 h-4 text-[#1fa5a3]" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{p.date}</p>
                    <h3 className="text-xl font-semibold text-[#14213d] group-hover:text-[#1fa5a3] leading-snug">{p.title}</h3>
                    <p className="text-gray-500 mt-2 leading-relaxed">{p.excerpt}</p>
                  </article>
                </Link>
              ))}
            </div>
            <Pagination page={page} pageCount={pageCount} onPageChange={changePage} pageSize={pageSize} onPageSizeChange={setPageSize} />
          </div>
        </div>
      </div>
    </div>
  );
}
